<template>
    <UApp :locale="uiLocale">
        <!--
            Nothing of the app renders before the first navigation has been decided (V17, §9.1).
            Until then `route` is vue-router's START_LOCATION — no name, no `meta`, no matched
            record — and picking a layout from its empty `meta` picked `Default`, i.e. the
            dashboard shell, for a visitor nobody had identified yet. That shell mounts the
            fleet cache and the 15 s dashboard poll, so a signed-out visit fired three
            authenticated requests before the guard had said a word about the session.
        -->
        <Loading v-if="!navigationDecided" />

        <!-- The layouts render the `<router-view>` themselves (vue-spec templates/shell). -->
        <component :is="layout" v-else />
    </UApp>
</template>

<script lang="ts" setup>
    import { computed, ref } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import { useI18n } from 'vue-i18n'
    import * as locales from '@nuxt/ui/locale'

    import Default from '@/layouts/Default.vue'
    import Blank   from '@/layouts/Blank.vue'
    import Loading from '@/layouts/Loading.vue'
    import { useAppStore } from '@/stores/app'

    const route    = useRoute()
    const router   = useRouter()
    const appStore = useAppStore()
    const { locale } = useI18n()

    // Keep Nuxt UI's built-in component texts in sync with the vue-i18n locale.
    // Fall back to English if the locale is not shipped by @nuxt/ui/locale.
    const uiLocale = computed(() => (locales as Record<string, typeof locales.en>)[locale.value] ?? locales.en)

    const layouts = { Default, Blank }
    const layout  = computed(() => layouts[(route.meta.layout as keyof typeof layouts) ?? 'Default'])

    /**
     * `router.isReady()` resolves when the **first** navigation has settled — after the guard has
     * awaited `/auth/me` and after any redirect it returned, so it is exactly "we know which screen
     * this is". It never rejects into a hang: a failed initial navigation still marks the router
     * ready, and the second callback covers it, because a page stuck on a spinner would trade one
     * silent failure for another.
     *
     * The session flag is anded in for the deep-linked signed-in case: the guard lets a matched
     * route through the moment the session answers, and this keeps the shell from drawing itself
     * a frame before the store it reads from has been filled.
     */
    const routerReady = ref(false)
    void router.isReady().then(() => { routerReady.value = true }, () => { routerReady.value = true })

    const navigationDecided = computed(() => routerReady.value && appStore.session.ready)
</script>
